import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { STATS } from "@/data/content";
import { fadeIn, fadeUp, staggered } from "@/lib/motion";

const easeOut = (p) => 1 - Math.pow(1 - p, 3);

const useCountUp = (value, active, duration = 1400) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    let frame;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / duration, 1);
      setN(Math.round(value * easeOut(p)));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, value, duration]);
  return n;
};

const CountUp = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const n = useCountUp(value, inView);
  return (
    <span ref={ref}>
      {n}
      {suffix}
    </span>
  );
};

export const Stats = () => (
  <section data-testid="stats-section" className="bg-midnight py-20 text-white sm:py-28">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
        <motion.div {...fadeUp}>
          <h2 data-testid="stats-headline" className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Results, Not Promises</h2>
          <p className="mt-4 font-heading text-lg font-semibold sm:text-xl text-white/90">Raising the standard for what a recruiting partner should be.</p>
          <p className="mt-6 max-w-md text-base leading-relaxed text-white/70">
            Transparent communication, real accountability, and hires that stay. The numbers speak for themselves, and so do our clients.
          </p>
          <Button asChild data-testid="stats-client-stories-link" className="mt-8 h-12 bg-white px-6 text-base text-navy hover:bg-mist active:scale-95">
            <Link to="/client-stories">See the stories behind the numbers <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </motion.div>

        <div className="grid grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-3">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={`stat-item-${i + 1}`}
              {...staggered({ ...fadeIn, transition: { duration: 0.5 } }, i)}
              className="bg-midnight p-6 sm:p-8"
            >
              <p className="font-heading text-3xl font-bold text-white sm:text-4xl">
                <CountUp value={s.value} suffix={s.suffix} />
              </p>
              <p className="mt-3 text-sm leading-snug text-white/70">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
